<template v-slot:item="data">
    <q-card
        @click="$emit('select')"
        class="full-width q-pa-sm q-mb-sm"
        :class="{
            'cause-card--selectable': isSelectable,
            'cause-card--selected': isSelected
        }"
        bordered
        flat
    >
        <q-card-section horizontal>
            <div class="full-width ellipsis">
                <!-- Fields -->
                <div>
                    <div class="text-h6 q-mb-xs">
                        {{ name }}
                    </div>
                    <div class="q-mb-xs text-subtitle2 cause-card__subtitle">
                        {{ $t(getLossOptions(lossType)['label']) }}
                        {{ formatCategoryName(categoryName) }}
                    </div>
                    <div
                        v-if="description.length > 0"
                        class="q-mb-xs cause-card__description"
                    >
                        {{ description }}
                    </div>
                    <div v-else class="q-mb-lg"></div>

                    <q-separator class="q-my-sm"></q-separator>
                    <div class="row">
                        <div
                            class="col-sm-6 col-xs-12 text-uppercase cause-card__subtitle"
                            :class="
                                $q.screen.gt.xs
                                    ? 'text-left q-pt-md'
                                    : 'text-center'
                            "
                        >
                            <q-icon
                                class="q-mr-xs"
                                :name="
                                    planned
                                        ? 'fas fa-check-circle'
                                        : 'fas fa-times-circle'
                                "
                                :color="planned ? 'positive' : 'negative'"
                                size="xs"
                            ></q-icon>
                            {{ planned ? $t('Planned') : $t('Unplanned') }}
                        </div>
                        <div
                            v-if="planned"
                            class="col-sm-6 col-xs-12 q-pt-md text-uppercase cause-card__subtitle"
                            :class="
                                $q.screen.gt.xs ? 'text-left' : 'text-center'
                            "
                        >
                            <q-icon
                                name="far fa-clock"
                                class="q-mr-sm"
                                size="xs"
                                color="grey"
                            >
                                <q-tooltip
                                    anchor="bottom middle"
                                    self="top middle"
                                >
                                    {{ $t('Target downtime') }}
                                </q-tooltip>
                            </q-icon>
                            <div class="target-downtime">
                                {{ formatTargetDowntime(targetDowntime) }}
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="equipmentFailure"
                        class="row q-pt-md text-uppercase cause-card__subtitle"
                    >
                        <div
                            class="col-sm-6 col-xs-12"
                            :class="
                                $q.screen.gt.xs ? 'text-left' : 'text-center'
                            "
                        >
                            <q-icon
                                class="q-mr-xs"
                                name="fa-solid fa-sensor-triangle-exclamation"
                                color="secondary"
                                size="xs"
                            />
                            {{ $t('Equipment failure') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <q-separator vertical></q-separator>
            <q-card-actions
                class="col-auto justify-around q-pl-xs q-pr-none"
                align="right"
                vertical
            >
                <q-card-actions
                    v-if="isSelectable"
                    class="col-auto justify-around q-pl-xs q-pr-none"
                    align="right"
                    vertical
                >
                    <q-icon
                        v-if="isSelected"
                        name="fas fa-check-square"
                        color="primary"
                        size="md"
                    >
                    </q-icon>
                    <q-icon
                        v-else
                        name="fal fa-square"
                        color="primary"
                        size="md"
                    >
                    </q-icon>
                </q-card-actions>
                <q-card-actions
                    v-else
                    class="col-auto justify-around q-pl-xs q-pr-none"
                    align="right"
                    vertical
                >
                    <q-separator vertical></q-separator>
                    <q-btn
                        @click="$emit('edit')"
                        class="q-pa-sm card__button--edit"
                        color="accent"
                        icon="fas fa-edit"
                        size="md"
                        flat
                    />
                    <q-btn
                        @click="$emit('delete')"
                        :disable="$userCannot('delete', 'causes')"
                        :color="
                            $userCannot('delete', 'causes') ? 'grey' : 'red-6'
                        "
                        class="q-pa-sm card__button--delete"
                        icon="fas fa-trash"
                        size="md"
                        flat
                    />
                </q-card-actions>
            </q-card-actions>
        </q-card-section>
    </q-card>
</template>

<script>
import { getLossOptions } from '@models/causes.js';
import { convertDurationToDHMS } from '@utils/time.js';

export default {
    name: 'CauseCard',
    emits: ['select', 'edit', 'delete'],
    props: {
        /**
         * Cause fields.
         */
        name: {
            type: String,
            required: true
        },
        lossType: {
            type: String,
            required: true
        },
        categoryName: {
            validator(value) {
                return typeof value === 'string' || value === null;
            },
            required: true
        },
        planned: {
            type: Boolean,
            required: true
        },
        description: {
            type: String,
            required: false,
            default() {
                return '';
            }
        },
        targetDowntime: {
            // unit expected is in minutes
            type: Number,
            required: false,
            default() {
                return 0;
            }
        },
        /**
         * Card options.
         */
        // is the card currently selected?
        isSelected: {
            type: Boolean,
            required: false
        },
        // should cards be selectable?
        isSelectable: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        equipmentFailure: {
            type: Boolean,
            required: true
        }
    },
    methods: {
        /**
         * If a category is given for this cause, prefix it with '•' so that in the UI
         * subtitle we have the string {type of loss} • {category}.
         * @param {String} category
         * @return {String} Formatted category name
         */
        formatCategoryName(category) {
            if (category) {
                return `• ${category}`;
            } else {
                return '';
            }
        },
        /**
         * Target downtime in DHMS.
         * @param {Number} minutes
         * @return {String} Target downtime in DHMS
         */
        formatTargetDowntime(minutes) {
            return convertDurationToDHMS(minutes * 60);
        },
        getLossOptions
    }
};
</script>

<style lang="scss">
.cause-card__subtitle {
    color: $grey-8;
}

.cause-card__description {
    color: $grey-7;
}

.cause-card--selectable {
    cursor: pointer;
}

.cause-card--selected {
    background-color: $grey-3;
}

.target-downtime {
    display: inline-block;
}
</style>
